import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import GardenModal from './GardenModal';
import { getSeverityInfo } from '../utils/securityUtils';
import './SecurityDashboard.css';

const API = process.env.REACT_APP_API_URL;

const MONITORED_STACK = [
  { component: 'Node.js',    version: '24.15.0', keyword: 'node'       },
  { component: 'React',      version: '19.2.1',  keyword: 'react'      },
  { component: 'PostgreSQL', version: '15.4',    keyword: 'postgresql' },
  { component: 'Docker',     version: '29.1.3',  keyword: 'docker'     },
  { component: 'Ubuntu',     version: '22.04',   keyword: 'ubuntu'     },
  { component: 'Caddy',      version: 'latest',  keyword: 'caddy'      },
  { component: 'axios',      version: '1.15.2',  keyword: 'axios'      },
  { component: 'Capacitor',  version: '8.2.0',   keyword: 'capacitor'  },
];

const FILTER_TABS = [
  { key: 'active',         label: 'Active'         },
  { key: 'not-applicable', label: 'Not Applicable' },
  { key: 'resolved',       label: 'Resolved'       },
  { key: 'all',            label: 'All'            },
];

const THRESHOLD_OPTIONS = [
  { value: 'critical', label: 'Critical only' },
  { value: 'high',     label: 'High+'         },
  { value: 'medium',   label: 'Medium+'       },
  { value: 'all',      label: 'All'           },
];

const THRESHOLD_STORAGE_KEY = 'yardray_cve_alert_threshold';

const LOG_ACTION_CLASSES = {
  acknowledge:    'audit-action--acknowledge',
  resolve:        'audit-action--resolve',
  delete:         'audit-action--delete',
  not_applicable: 'audit-action--not-applicable',
  check_run:      'audit-action--check-run',
  manual_create:  'audit-action--manual-create',
  edit:           'audit-action--edit',
};

function getSeverityClass(severity) {
  return `severity-${(severity || 'unknown').toLowerCase()}`;
}

function SecurityDashboard() {
  const [alerts, setAlerts]                     = useState([]);
  const [summary, setSummary]                   = useState({ critical: 0, high: 0, medium: 0, low: 0 });
  const [lastChecked, setLastChecked]           = useState(null);
  const [npmAudit, setNpmAudit]                 = useState(null);
  const [isNpmAuditLoading, setIsNpmAuditLoading] = useState(true);
  const [auditLog, setAuditLog]                 = useState([]);
  const [isLoading, setIsLoading]               = useState(true);
  const [error, setError]                       = useState(null);
  const [activeTab, setActiveTab]               = useState('active');
  const [severityFilter, setSeverityFilter]     = useState(null);
  const [isCheckRunning, setIsCheckRunning]     = useState(false);
  const [threshold, setThreshold]               = useState(
    () => localStorage.getItem(THRESHOLD_STORAGE_KEY) || 'high'
  );
  const [modal, setModal]                       = useState({ isOpen: false });
  const [modalFormData, setModalFormData]       = useState({});
  const [expandedAlerts, setExpandedAlerts]     = useState(new Set());
  const [isManualAddOpen, setIsManualAddOpen]   = useState(false);
  const [isAuditLogOpen, setIsAuditLogOpen]     = useState(false);
  const alertsSectionRef = useRef(null);
  const [manualForm, setManualForm]             = useState({
    cveId: '', component: '', version: '', severity: 'HIGH', cvssScore: '', description: '',
  });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchAlerts = async () => {
    try {
      const res = await axios.get(`${API}/admin/security`, { headers: getHeaders() });
      setAlerts(res.data.alerts || []);
      setSummary(res.data.summary || { critical: 0, high: 0, medium: 0, low: 0 });
      setLastChecked(res.data.lastChecked);
    } catch (err) {
      console.error('SecurityDashboard: fetchAlerts failed:', err);
      setError(err.response?.status === 403 ? 'Access denied' : 'Failed to load security alerts');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNpmAudit = async () => {
    setIsNpmAuditLoading(true);
    try {
      const res = await axios.get(`${API}/admin/security/npm-audit`, { headers: getHeaders() });
      setNpmAudit(res.data);
    } catch (err) {
      console.error('SecurityDashboard: fetchNpmAudit failed:', err);
      setNpmAudit({ error: 'Failed to load npm audit results' });
    } finally {
      setIsNpmAuditLoading(false);
    }
  };

  const fetchAuditLog = async () => {
    try {
      const res = await axios.get(`${API}/admin/security/audit-log`, { headers: getHeaders() });
      setAuditLog(res.data.log || []);
    } catch (err) {
      console.error('SecurityDashboard: fetchAuditLog failed:', err);
    }
  };

  useEffect(() => {
    fetchAlerts();
    fetchNpmAudit();
    fetchAuditLog();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRunCheck = async () => {
    setIsCheckRunning(true);
    try {
      await axios.post(`${API}/admin/security/check`, {}, { headers: getHeaders() });
      await fetchAlerts();
      await fetchAuditLog();
    } catch (err) {
      console.error('SecurityDashboard: handleRunCheck failed:', err);
    } finally {
      setIsCheckRunning(false);
    }
  };

  const handleThresholdChange = (e) => {
    const val = e.target.value;
    setThreshold(val);
    localStorage.setItem(THRESHOLD_STORAGE_KEY, val);
  };

  const toggleExpanded = (id) => {
    setExpandedAlerts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const optimisticStatusUpdate = (id, newStatus) =>
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, status: newStatus } : a));

  const revertAlert = (id, original) =>
    setAlerts(prev => prev.map(a => a.id === id ? original : a));

  const showErrorModal = (message) =>
    setModal({ isOpen: true, kind: 'error', type: 'alert', title: 'Error', message });

  const handleAcknowledge = (alert) => {
    setModalFormData({ note: '' });
    setModal({ isOpen: true, kind: 'acknowledge', alertId: alert.id, type: 'confirm', title: 'Acknowledge Alert' });
  };

  const handleResolve = (alert) => {
    setModal({ isOpen: true, kind: 'resolve', alertId: alert.id, type: 'confirm', title: 'Mark as Resolved' });
  };

  const handleNotApplicable = (alert) => {
    setModalFormData({ reason: '' });
    setModal({ isOpen: true, kind: 'not-applicable', alertId: alert.id, type: 'confirm', title: 'Mark as Not Applicable' });
  };

  const handleEdit = (alert) => {
    setModalFormData({ severity: alert.severity, notes: alert.notes || '' });
    setModal({ isOpen: true, kind: 'edit', alertId: alert.id, type: 'confirm', title: 'Edit Alert' });
  };

  const handleDelete = (alert) => {
    setModal({ isOpen: true, kind: 'delete', alertId: alert.id, deleteTarget: alert, type: 'confirm', title: 'Delete Alert' });
  };

  const handleModalConfirm = async () => {
    const { kind, alertId, deleteTarget } = modal;
    setModal(prev => ({ ...prev, isOpen: false }));

    if (kind === 'acknowledge') {
      const original = alerts.find(a => a.id === alertId);
      optimisticStatusUpdate(alertId, 'acknowledged');
      try {
        await axios.post(`${API}/admin/security/${alertId}/acknowledge`, { note: modalFormData.note || '' }, { headers: getHeaders() });
        fetchAuditLog();
      } catch (err) {
        revertAlert(alertId, original);
        showErrorModal('Failed to acknowledge alert');
      }

    } else if (kind === 'resolve') {
      const original = alerts.find(a => a.id === alertId);
      optimisticStatusUpdate(alertId, 'resolved');
      try {
        await axios.post(`${API}/admin/security/${alertId}/resolve`, {}, { headers: getHeaders() });
        fetchAuditLog();
      } catch (err) {
        revertAlert(alertId, original);
        showErrorModal('Failed to resolve alert');
      }

    } else if (kind === 'not-applicable') {
      const original = alerts.find(a => a.id === alertId);
      optimisticStatusUpdate(alertId, 'not_applicable');
      try {
        await axios.post(`${API}/admin/security/${alertId}/not-applicable`, { reason: modalFormData.reason || '' }, { headers: getHeaders() });
        fetchAuditLog();
      } catch (err) {
        revertAlert(alertId, original);
        showErrorModal('Failed to mark alert as not applicable');
      }

    } else if (kind === 'edit') {
      const original = alerts.find(a => a.id === alertId);
      setAlerts(prev => prev.map(a => a.id === alertId
        ? { ...a, severity: modalFormData.severity, notes: modalFormData.notes }
        : a
      ));
      try {
        await axios.put(`${API}/admin/security/${alertId}`, {
          severity: modalFormData.severity,
          notes:    modalFormData.notes,
        }, { headers: getHeaders() });
        fetchAuditLog();
      } catch (err) {
        revertAlert(alertId, original);
        showErrorModal('Failed to update alert');
      }

    } else if (kind === 'delete') {
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      if (deleteTarget?.status === 'active') {
        const sev = deleteTarget.severity?.toLowerCase();
        setSummary(prev => ({ ...prev, [sev]: Math.max(0, (prev[sev] || 0) - 1) }));
      }
      try {
        await axios.delete(`${API}/admin/security/${alertId}`, { headers: getHeaders() });
        fetchAuditLog();
      } catch (err) {
        fetchAlerts();
        showErrorModal('Failed to delete alert');
      }
    }
  };

  const handleModalCancel = () => setModal({ isOpen: false });

  const handleManualCreate = async () => {
    if (!/^CVE-\d{4}-\d{4,}$/i.test(manualForm.cveId)) {
      showErrorModal('Invalid CVE ID format. Expected: CVE-YYYY-NNNNN');
      return;
    }
    try {
      await axios.post(`${API}/admin/security`, {
        cve_id:      manualForm.cveId,
        component:   manualForm.component,
        version:     manualForm.version,
        severity:    manualForm.severity,
        cvss_score:  manualForm.cvssScore || null,
        description: manualForm.description,
      }, { headers: getHeaders() });
      setManualForm({ cveId: '', component: '', version: '', severity: 'HIGH', cvssScore: '', description: '' });
      setIsManualAddOpen(false);
      fetchAlerts();
      fetchAuditLog();
    } catch (err) {
      showErrorModal(err.response?.data?.error || 'Failed to create alert');
    }
  };

  const renderModalContent = () => {
    if (modal.kind === 'acknowledge') {
      return (
        <div className="security-modal-form">
          <p className="security-modal-desc">Acknowledge this alert? Add an optional note:</p>
          <textarea
            className="security-modal-textarea"
            value={modalFormData.note || ''}
            onChange={e => setModalFormData(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Optional note..."
            rows={3}
          />
        </div>
      );
    }
    if (modal.kind === 'not-applicable') {
      return (
        <div className="security-modal-form">
          <p className="security-modal-desc">Mark as not applicable to your stack?</p>
          <textarea
            className="security-modal-textarea"
            value={modalFormData.reason || ''}
            onChange={e => setModalFormData(prev => ({ ...prev, reason: e.target.value }))}
            placeholder="Optional reason..."
            rows={3}
          />
        </div>
      );
    }
    if (modal.kind === 'edit') {
      return (
        <div className="security-modal-form">
          <label className="security-modal-label">Severity</label>
          <select
            className="security-modal-select"
            value={modalFormData.severity || 'HIGH'}
            onChange={e => setModalFormData(prev => ({ ...prev, severity: e.target.value }))}
          >
            {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'].map(s => (
              <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
            ))}
          </select>
          <label className="security-modal-label">Notes</label>
          <textarea
            className="security-modal-textarea"
            value={modalFormData.notes || ''}
            onChange={e => setModalFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Notes..."
            rows={3}
          />
        </div>
      );
    }
    if (modal.kind === 'delete' && modal.deleteTarget) {
      return `Permanently delete ${modal.deleteTarget.cve_id}? This cannot be undone.`;
    }
    if (modal.kind === 'resolve') {
      return 'Mark this alert as resolved?';
    }
    return modal.message || null;
  };

  const filteredAlerts = alerts.filter(a => {
    const tabMatch =
      activeTab === 'all'            ? true :
      activeTab === 'active'         ? (a.status === 'active' || a.status === 'acknowledged') :
      activeTab === 'not-applicable' ? a.status === 'not_applicable' :
      activeTab === 'resolved'       ? a.status === 'resolved' : true;
    const severityMatch = severityFilter ? a.severity === severityFilter : true;
    return tabMatch && severityMatch;
  });

  const isScanStale    = !lastChecked || (Date.now() - new Date(lastChecked).getTime()) > 48 * 3_600_000;
  const formatDate     = (ts) => ts
    ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
  const getStackCveCount = (keyword) =>
    alerts.filter(a =>
      (a.status === 'active' || a.status === 'acknowledged') &&
      a.component?.toLowerCase().includes(keyword)
    ).length;

  if (isLoading) return <div className="security-loading">Loading security data...</div>;
  if (error)     return <div className="security-error">{error}</div>;

  return (
    <div className="security-dashboard">
      <GardenModal
        isOpen={modal.isOpen}
        type={modal.type}
        title={modal.title}
        message={renderModalContent()}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />

      {/* HEADER */}
      <div className="security-card">
        <div className="security-header-row">
          <div className="security-header-left">
            <h3 className="security-title">Security & CVE Monitor</h3>
            <div className="security-scan-meta">
              <span className={`security-health-dot ${isScanStale ? 'security-health-dot--stale' : 'security-health-dot--fresh'}`} />
              <span className="security-last-scan">
                {lastChecked ? `Last scan: ${formatDate(lastChecked)}` : 'No scan recorded'}
              </span>
            </div>
          </div>
          <div className="security-header-right">
            <label className="security-threshold-label" htmlFor="alert-threshold">
              Email alerts:
            </label>
            <select
              id="alert-threshold"
              className="security-threshold-select"
              value={threshold}
              onChange={handleThresholdChange}
            >
              {THRESHOLD_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              className="security-run-btn"
              onClick={handleRunCheck}
              disabled={isCheckRunning}
            >
              {isCheckRunning ? <span className="security-spinner" /> : 'Run Security Check'}
            </button>
          </div>
        </div>
      </div>

      {/* SUMMARY CARDS */}
      <div className="security-summary-row">
        {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].map(sev => {
          const info     = getSeverityInfo(sev);
          const count    = summary[sev.toLowerCase()] || 0;
          const isActive = severityFilter === sev;
          return (
            <button
              key={sev}
              className={`security-summary-card security-summary-card--${sev.toLowerCase()}${isActive ? ' security-summary-card--selected' : ''}`}
              onClick={() => setSeverityFilter(isActive ? null : sev)}
            >
              <div className="security-summary-count">{count}</div>
              <div className="security-summary-label">{info.label}</div>
            </button>
          );
        })}
      </div>

      {/* NPM AUDIT */}
      <div className="security-card">
        <h4 className="security-section-title">Dependency Vulnerabilities (npm audit)</h4>
        {isNpmAuditLoading ? (
          <div className="security-section-loading">Running npm audit...</div>
        ) : npmAudit?.error ? (
          <div className="security-section-error">{npmAudit.error}</div>
        ) : !npmAudit?.vulnerabilities?.length ? (
          <div className="security-empty">
            <span className="security-empty-icon">✓</span> No dependency vulnerabilities found
          </div>
        ) : (
          <>
            <div className="security-npm-meta">
              {npmAudit.metadata?.vulnerabilities && (
                <span className="security-npm-total">
                  {Object.values(npmAudit.metadata.vulnerabilities).reduce((a, b) => a + b, 0)} total vulnerabilities
                </span>
              )}
              <span className="security-npm-date">as of {formatDate(npmAudit.fetchedAt)}</span>
            </div>
            <div className="security-npm-list">
              {npmAudit.vulnerabilities.map((v, i) => (
                <div key={i} className="security-npm-row">
                  <span className={`security-severity-badge ${getSeverityClass(v.severity)}`}>
                    {getSeverityInfo(v.severity).label}
                  </span>
                  <span className="security-npm-name">{v.name}</span>
                  <span className="security-npm-range">{v.range}</span>
                  {v.fixAvailable && (
                    <span className="security-npm-fix-tag">Fix Available</span>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* STACK VERSIONS TABLE */}
      <div className="security-card">
        <h4 className="security-section-title">Stack Versions</h4>
        <table className="security-stack-table">
          <thead>
            <tr>
              <th className="security-table-th">Component</th>
              <th className="security-table-th">Current Version</th>
              <th className="security-table-th">Active CVEs</th>
            </tr>
          </thead>
          <tbody>
            {MONITORED_STACK.map(({ component, version, keyword }) => {
              const count = getStackCveCount(keyword);
              return (
                <tr key={component} className="security-table-row">
                  <td className="security-table-td">{component}</td>
                  <td className="security-table-td security-table-td--version">{version}</td>
                  <td className="security-table-td">
                    {count > 0 ? (
                      <button
                        className="security-cve-count-btn"
                        onClick={() => {
                          setActiveTab('active');
                          setSeverityFilter(null);
                          alertsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >
                        {count} alert{count !== 1 ? 's' : ''}
                      </button>
                    ) : (
                      <span className="security-cve-count-clear">Clear</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* CVE ALERTS LIST */}
      <div className="security-card" ref={alertsSectionRef}>
        <div className="security-alerts-header">
          <h4 className="security-section-title">CVE Alerts</h4>
          {severityFilter && (
            <button className="security-clear-filter-btn" onClick={() => setSeverityFilter(null)}>
              ✕ {severityFilter}
            </button>
          )}
        </div>
        <div className="security-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              className={`security-tab${activeTab === tab.key ? ' security-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {filteredAlerts.length === 0 ? (
          <div className="security-empty">
            <span className="security-empty-icon">✓</span> No alerts in this view
          </div>
        ) : (
          <div className="security-alerts-list">
            {filteredAlerts.map(alert => {
              const info       = getSeverityInfo(alert.severity);
              const isExpanded = expandedAlerts.has(alert.id);
              const statusSlug = (alert.status || 'active').replace(/_/g, '-');
              return (
                <div key={alert.id} className={`security-alert-card security-alert-card--${(alert.severity || 'unknown').toLowerCase()}`}>
                  <div className="security-alert-top">
                    <div className="security-alert-meta">
                      <a
                        href={`https://nvd.nist.gov/vuln/detail/${alert.cve_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="security-cve-link"
                      >
                        {alert.cve_id}
                      </a>
                      <span className={`security-severity-badge ${getSeverityClass(alert.severity)}`}>
                        {info.label}
                      </span>
                      {alert.cvss_score && (
                        <span className="security-cvss">CVSS {Number(alert.cvss_score).toFixed(1)}</span>
                      )}
                      <span className="security-component">
                        {alert.component} {alert.version}
                      </span>
                    </div>
                    <span className={`security-status-badge security-status-badge--${statusSlug}`}>
                      {(alert.status || 'active').replace(/_/g, ' ')}
                    </span>
                  </div>

                  {alert.description && (
                    <>
                      <div className={`security-alert-desc${isExpanded ? ' security-alert-desc--expanded' : ''}`}>
                        {alert.description}
                      </div>
                      {alert.description.length > 120 && (
                        <button className="security-expand-btn" onClick={() => toggleExpanded(alert.id)}>
                          {isExpanded ? 'Show less' : 'Show more'}
                        </button>
                      )}
                    </>
                  )}

                  {alert.notes && (
                    <div className="security-alert-notes">Note: {alert.notes}</div>
                  )}

                  <div className="security-alert-footer">
                    <span className="security-alert-date">
                      Published {formatDate(alert.published_date)}
                    </span>
                    <div className="security-alert-actions">
                      {alert.status === 'active' && (
                        <button className="security-action-btn security-action-btn--acknowledge" onClick={() => handleAcknowledge(alert)}>
                          Acknowledge
                        </button>
                      )}
                      {(alert.status === 'active' || alert.status === 'acknowledged') && (
                        <>
                          <button className="security-action-btn security-action-btn--not-applicable" onClick={() => handleNotApplicable(alert)}>
                            Not Applicable
                          </button>
                          <button className="security-action-btn security-action-btn--resolve" onClick={() => handleResolve(alert)}>
                            Resolve
                          </button>
                        </>
                      )}
                      <button className="security-action-btn security-action-btn--edit" onClick={() => handleEdit(alert)}>
                        Edit
                      </button>
                      <button className="security-action-btn security-action-btn--delete" onClick={() => handleDelete(alert)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* RECENT SECURITY EVENTS */}
      <div className="security-card">
        <button
          className="security-manual-toggle"
          onClick={() => setIsAuditLogOpen(prev => !prev)}
        >
          {isAuditLogOpen ? '▲ Hide Recent Events' : '▼ Show Recent Events'}
        </button>
        {isAuditLogOpen && (
          auditLog.length === 0 ? (
            <div className="security-empty">No security events recorded</div>
          ) : (
            <div className="security-audit-list">
              {auditLog.map(entry => (
                <div key={entry.id} className="security-audit-row">
                  <span className={`security-audit-action ${LOG_ACTION_CLASSES[entry.action] || ''}`}>
                    {(entry.action || '').replace(/_/g, ' ')}
                  </span>
                  <span className="security-audit-entity">
                    {entry.entity_type}{entry.entity_id ? ` #${entry.entity_id}` : ''}
                  </span>
                  <span className="security-audit-by">{entry.performed_by}</span>
                  <span className="security-audit-date">{formatDate(entry.created_at)}</span>
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* MANUAL ADD CVE */}
      <div className="security-card">
        <button
          className="security-manual-toggle"
          onClick={() => setIsManualAddOpen(prev => !prev)}
        >
          {isManualAddOpen ? '▲ Hide' : '▼ Manually Add CVE'}
        </button>
        {isManualAddOpen && (
          <div className="security-manual-form">
            <div className="security-form-row">
              <label className="security-form-label">CVE ID *</label>
              <input
                className="security-form-input"
                placeholder="CVE-YYYY-NNNNN"
                value={manualForm.cveId}
                onChange={e => setManualForm(prev => ({ ...prev, cveId: e.target.value }))}
              />
            </div>
            <div className="security-form-row">
              <label className="security-form-label">Component *</label>
              <select
                className="security-form-select"
                value={manualForm.component}
                onChange={e => setManualForm(prev => ({ ...prev, component: e.target.value }))}
              >
                <option value="">Select component...</option>
                {MONITORED_STACK.map(s => (
                  <option key={s.component} value={s.keyword}>{s.component}</option>
                ))}
              </select>
            </div>
            <div className="security-form-row">
              <label className="security-form-label">Version</label>
              <input
                className="security-form-input"
                placeholder="e.g. 24.15.0"
                value={manualForm.version}
                onChange={e => setManualForm(prev => ({ ...prev, version: e.target.value }))}
              />
            </div>
            <div className="security-form-row">
              <label className="security-form-label">Severity *</label>
              <select
                className="security-form-select"
                value={manualForm.severity}
                onChange={e => setManualForm(prev => ({ ...prev, severity: e.target.value }))}
              >
                {['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNKNOWN'].map(s => (
                  <option key={s} value={s}>{s.charAt(0) + s.slice(1).toLowerCase()}</option>
                ))}
              </select>
            </div>
            <div className="security-form-row">
              <label className="security-form-label">CVSS Score</label>
              <input
                className="security-form-input"
                type="number"
                min="0"
                max="10"
                step="0.1"
                placeholder="0.0 – 10.0"
                value={manualForm.cvssScore}
                onChange={e => setManualForm(prev => ({ ...prev, cvssScore: e.target.value }))}
              />
            </div>
            <div className="security-form-row">
              <label className="security-form-label">Description</label>
              <textarea
                className="security-form-textarea"
                rows={3}
                placeholder="CVE description..."
                value={manualForm.description}
                onChange={e => setManualForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
            <div className="security-form-actions">
              <button className="security-form-submit-btn" onClick={handleManualCreate}>
                Add Alert
              </button>
              <button
                className="security-form-cancel-btn"
                onClick={() => {
                  setIsManualAddOpen(false);
                  setManualForm({ cveId: '', component: '', version: '', severity: 'HIGH', cvssScore: '', description: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SecurityDashboard;
